import { getFieldFromPushData, getFieldsFormSession, getMatchingFieldsFromSession } from "../../util/functions";

import { Answer } from "../Steps/InputFields/InputFields.types";
import { IAppContext } from "../../util/context";
import { StepData } from "../FormViewer/FormViewer.types";
import { StepField } from "../Steps/Step.types";
import { Condition } from "../FormClient/FormClient.types";

export const handleScrollIntoView = () => {
  const element = document.getElementById("formClient");
  const header = document.querySelector(".header.sticky-top") as HTMLElement;

  if (element) {
    const scrollPosition =  header ? element.offsetTop - header.offsetHeight : element.offsetTop;
    window.scrollTo({
      top: scrollPosition - 15,
      behavior: "smooth",
    });
  }
};

export const handleDuplicateStep = (stepData: StepData) => {
  const { uuid } = stepData;

  const duplicateStepIndex = window.sessionLayer.lead.lead_data.findIndex((step: StepData) => step.uuid === uuid);
  if (duplicateStepIndex > -1) {
    window.sessionLayer.lead.lead_data[duplicateStepIndex] = stepData;
  } else {
    window.sessionLayer.lead.lead_data.push(stepData);
  }
}

export const getNextStepIndex = (activeStepIndex: number, stepData: StepData, steps: any, context: IAppContext) => {
  const { updatePrevStepIndex } = context;
  const { 
    settings: { next_step, last_step },
    answer = {} as Answer,
    uuid
  } = stepData;

  const { next_step_answer } = answer;

  const conditionalNextStep = next_step_answer || next_step;
  const prevStepIndex = steps.findIndex((step: StepField) => step.stepSettings[0]?.uuid === uuid);
  updatePrevStepIndex(prevStepIndex);

  if (conditionalNextStep) {
    // If the answer provided in the step has a next_step_answer, set the active step to the next step
    // OR if the step has a next_step, set the active step to that step
    
    return steps.findIndex((step: StepField) => step.stepSettings[0]?.uuid === conditionalNextStep);
  }
  else if (!last_step) {
    // If the step doesn't have a next_step, set the active step to the next step
    return activeStepIndex + 1;
  }   
  else if (last_step) {
    // Terminal last step with no explicit next_step/answer: the flow ends here
    // and must redirect to the success URL. Return -1 so the caller's
    // `hasSuccessStep` stays false (it would otherwise latch onto an unrelated
    // in-app success page elsewhere in the form and suppress the redirect).
    return -1;
  }
  else {
    return 0;
  }
}

export const handleStatus = (response: {res: Response, details: string} | undefined, context: IAppContext): boolean => {
  const { updateDisableButton, updateErrorMessage } = context;
  if(response) {
    const { status } = response.res;
    if(status < 200 || status > 300){
      updateErrorMessage(response?.details);
      updateDisableButton(false);
      return false;
    }
  }
  updateDisableButton(false);
  return true;
}

export const handleBackClick = (context: IAppContext) => {
  const { steps, updateActiveStepIndex, updateBreadcrumb, activeStepIndex, breadcrumb } = context;
  const prevStepIndex = breadcrumb[breadcrumb.indexOf(activeStepIndex) - 1];
  const newBreadcrumb = breadcrumb.slice(0, breadcrumb.indexOf(prevStepIndex) + 1);
  updateBreadcrumb(newBreadcrumb);

  // The answer leaves with the step. Kept, an answer from an abandoned branch
  // keeps hiding steps on the branch the patient switches to (two insurance
  // questions share one CRM field) and lands in the lead as a second value.
  // Nothing is lost: the step has to be submitted again to move on.
  const leftUuid = steps[activeStepIndex]?.stepSettings[0]?.uuid;
  const { lead } = window.sessionLayer;
  lead.lead_data = lead.lead_data.filter((step: StepData) => step.uuid !== leftUuid);

  updateActiveStepIndex(prevStepIndex);
};

export const checkIfConditionsMet = (conditions: Array<Condition>) => {
  const conditionalFields: any[] = getMatchingFieldsFromSession(conditions);

  const existingEmailField = conditionalFields.find((item: any) => { return item.field_crm_id === "email" && item.field_session_source === "submitted_data" });
  
  const pushEmailToSession = existingEmailField && !getFieldFromPushData("email") && !getFieldFromPushData("contact_id");
  pushEmailToSession && window.sessionLayer.pushToSession("email", existingEmailField.field_value);
  
  return conditionalFields.some((field: any) => conditions.some((item: any) => item.field === field.field_crm_id));
}

// Where a hidden question leads: by the answer already in the session (e.g. a
// `crm_field_*` URL parameter), as if it had been clicked, else by its
// nextStep. Empty when neither routes anywhere.
const getHiddenQuestionNextUuid = (step: StepField): string => {
  const sessionValue = step.crmProperty && getFieldsFormSession(step.crmProperty);
  const answer = step.answers?.find((a) => a.crmFieldValue === sessionValue);
  return answer?.nextStepAnswer || step.stepSettings[0].nextStep;
};

// Walks on from `startIndex` past every step its hideIfFieldExists hides and
// returns the first one to show. A hidden question routes by its answer; any
// other hidden step falls through to the one after it — its nextStep says
// where it leads once *completed*, and the step behind it is the alternative
// (a "no result" page, the next product's variant). Hidden means hidden, last
// step or not; only the final step and a route to nowhere are shown even when
// hidden, so the form never runs off its end.
export const resolveVisibleStepIndex = (steps: Array<StepField>, startIndex: number): number => {
  let index = startIndex;
  const visited = new Set<number>();

  while (index >= 0 && index < steps.length - 1 && !visited.has(index)) {
    visited.add(index);
    const step = steps[index];
    if (!checkIfConditionsMet(step.stepSettings[0].hideIfFieldExists)) break;

    const nextUuid = step._modelApiKey === "step_question" ? getHiddenQuestionNextUuid(step) : "";
    const nextIndex = nextUuid ? steps.findIndex((s: StepField) => s.stepSettings[0]?.uuid === nextUuid) : index + 1;
    if (nextIndex < 0) break;
    index = nextIndex;
  }

  return index;
}
